/**
 * Translation utility supporting Tamil, Hindi, Kannada, Telugu, and Malayalam.
 * Uses robust multi-tier translation with fallback and paragraph chunking.
 */

export const SUPPORTED_LANGUAGES = [
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', label: 'Read in Tamil' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', label: 'Read in Hindi' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', label: 'Read in Kannada' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', label: 'Read in Telugu' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', label: 'Read in Malayalam' },
];

/**
 * Translates a single chunk of text (< 1000 characters).
 */
async function translateChunk(text, targetLang) {
  if (!text || !text.trim()) return text;

  // Try Primary: Google Translate GTX endpoint
  try {
    const gUrl = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${encodeURIComponent(
      targetLang
    )}&dt=t&q=${encodeURIComponent(text)}`;
    const response = await fetch(gUrl);
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data) && Array.isArray(data[0])) {
        const translated = data[0].map((item) => (item && item[0] ? item[0] : '')).join('');
        if (translated && translated.trim()) {
          return translated;
        }
      }
    }
  } catch (err) {
    console.warn('Google Translate API error, falling back to MyMemory:', err);
  }

  // Fallback: MyMemory API
  try {
    const mUrl = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(
      text
    )}&langpair=autodetect|${encodeURIComponent(targetLang)}`;
    const response = await fetch(mUrl);
    if (response.ok) {
      const data = await response.json();
      if (data && data.responseData && data.responseData.translatedText) {
        return data.responseData.translatedText;
      }
    }
  } catch (err) {
    console.warn('MyMemory API error:', err);
  }

  // If all fails, return original text
  return text;
}

/**
 * Translates any length of text by preserving newlines and splitting into manageable chunks.
 */
export async function translateText(text, targetLang) {
  if (!text || !text.trim()) return text;

  // If target is English or already matches, return original
  if (targetLang === 'en' || targetLang === 'original') return text;

  // Split by newlines to preserve formatting
  const paragraphs = text.split('\n');
  const translatedParagraphs = [];

  for (const para of paragraphs) {
    if (!para.trim()) {
      translatedParagraphs.push('');
      continue;
    }

    // If paragraph is extraordinarily long, split into sentences
    if (para.length > 800) {
      const sentences = para.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [para];
      const translatedSentences = [];
      for (const sentence of sentences) {
        const trans = await translateChunk(sentence, targetLang);
        translatedSentences.push(trans);
      }
      translatedParagraphs.push(translatedSentences.join(' '));
    } else {
      const trans = await translateChunk(para, targetLang);
      translatedParagraphs.push(trans);
    }
  }

  return translatedParagraphs.join('\n');
}

/**
 * Translates an entire news item (title and description) into the target language.
 */
export async function translateNewsItem(news, targetLang) {
  if (!news) return null;
  if (targetLang === 'original') {
    return {
      title: news.title,
      description: news.description,
      language: 'original',
    };
  }

  const [translatedTitle, translatedDescription] = await Promise.all([
    translateText(news.title, targetLang),
    translateText(news.description, targetLang),
  ]);

  return {
    title: translatedTitle,
    description: translatedDescription,
    language: targetLang,
  };
}
